import { LitElement, html, css } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { bindEvent, Handler } from '@gsmlg/utils/dist/browser/bindEvent';

@customElement('gsmlg-switch')
export class GSMLGSwitch extends LitElement {
  static override styles = css`
    :root {
      font-size: inherit;
      box-sizing: border-box;
    }
    div {
      display: inline-flex;
      align-items: center;
      height: 1.4em;
      box-sizing: border-box;
      vertical-align: middle;
    }
    span {
      margin-right: 0.7em;
      height: 1.4em;
      line-height: 1.4em;
    }

    input[type='radio'] {
      display: none;
    }

    .disabled {
      opacity: 0.4;
    }

    .disabled label {
      cursor: initial;
    }
    .readonly label {
      cursor: initial;
    }

    .checked label {
      background-color: var(--gsmlg-switch-checked-color, #476fd8);
    }
    .checked label:before {
      background-color: var(--gsmlg-switch-checked-c-color, #fff);
    }
    .unchecked label {
      background-color: var(--gsmlg-switch-unchecked-color, gray);
    }
    .unchecked label:before {
      background-color: var(--gsmlg-switch-unchecked-c-color, #bbb);
    }

    label {
      cursor: pointer;
      position: relative;
      color: white;
      width: 3em;
      height: 1.4em;
      line-height: 1.4em;
      border-radius: 1.4em;
      display: inline-flex;
    }

    label:before {
      position: absolute;
      content: ' ';
      left: 0.1em;
      top: 0.1em;
      width: 1.2em;
      height: 1.2em;
      line-height: 1.4em;
      border-radius: 1.2em;
      transition: all 0.4s;
    }

    .checked label:before {
      left: calc(3em - 1.2em - 0.1em);
    }

    label:after {
      position: relative;
      line-height: 2em;
      content: ' ';
      left: 5px;
      -webkit-transition: all 0.4s;
      -moz-transition: all 0.4s;
      transition: all 0.4s;
    }

    input.checked + label:after,
    input:checked + label:after {
      content: ' ';
      left: -15px;
    }
  `;

  @property({ type: String, reflect: true, attribute: 'label-text' })
  labelText: string = '';

  @property({ type: String, reflect: true, attribute: true })
  name: string = '';

  @property({ type: String, reflect: true, attribute: true })
  value: string = '';

  @property({ type: String, reflect: true, attribute: 'on-value' })
  onValue: string = 'true';

  @property({ type: String, reflect: true, attribute: 'off-value' })
  offValue: string = 'false';

  @property({ type: Boolean, reflect: true, attribute: true })
  checked: boolean = false;

  @property({ type: Boolean, reflect: true, attribute: true })
  disabled: boolean = false;

  @property({ type: Boolean, reflect: true, attribute: true })
  readonly: boolean = false;

  toggleAction() {
    if (this.readonly || this.disabled) return;
    this.checked = !this.checked;
    this.requestUpdate('checked', !this.checked);
  }

  _hFormdata?: Handler = undefined;
  _handleFormdata(event: Event) {
    const { formData } = event as FormDataEvent;
    const { disabled, name, value } = this;
    if (!disabled) {
      formData.append(name, value);
    }
  }

  override connectedCallback() {
    super.connectedCallback();
    const form = this.closest('form');
    if (form) {
      this._hFormdata = bindEvent(form, 'formdata', this._handleFormdata.bind(this));
    }

    if (this.value) {
      if (this.value === this.onValue) {
        this.checked = true;
        this.requestUpdate('checked');
      } else {
        this.checked = false;
        this.requestUpdate('checked');
      }
    } else if (this.checked) {
      this.value = this.onValue;
      this.requestUpdate('value', this.offValue);
    } else {
      this.value = this.offValue;
      this.requestUpdate('value', this.onValue);
    }
  }

  override disconnectedCallback() {
    if (this._hFormdata) {
      this._hFormdata = this._hFormdata.release();
    }

    super.disconnectedCallback();
  }

  override attributeChangedCallback(
    name: string,
    old: string | null,
    value: string | null,
  ) {
    super.attributeChangedCallback(name, old, value);
    if (name === 'checked') {
      this.requestUpdate('checked', old);
      if (this.checked) {
        this.value = this.onValue;
        this.requestUpdate('value', this.offValue);
      } else {
        this.value = this.offValue;
        this.requestUpdate('value', this.onValue);
      }
    }
    if (name === 'disabled') {
      this.requestUpdate('disabled', old);
    }
    if (name === 'readonly') {
      this.requestUpdate('readonly', old);
    }
  }

  override render() {
    return html`
      <div
        @click=${() => this.toggleAction()}
        class=${classMap({
          checked: this.checked,
          unchecked: !this.checked,
          disabled: this.disabled,
          readonly: this.readonly,
        })}
      >
        <span>${this.labelText}</span>
        <label></label>
        <input
          type="radio"
          name=${this.name}
          value=${this.onValue}
          ?checked=${this.checked}
        />
        <input
          type="radio"
          name=${this.name}
          value=${this.offValue}
          ?checked=${!this.checked}
        />
      </div>
    `;
  }
}
